import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandProvider } from "./context/BrandContext";
import { DashboardLayout } from "./components/DashboardLayout";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import ScriptGenerator from "./pages/ScriptGenerator";
import ImageGenerator from "./pages/ImageGenerator";
import AudioGenerator from "./pages/AudioGenerator";
import VideoGenerator from "./pages/VideoGenerator";
import ContentLibrary from "./pages/ContentLibrary";
import ImageEditor from "./pages/ImageEditor";
import Subscription from "./pages/Subscription";
import Settings from "./pages/Settings";
import Labs from "./pages/Labs";
import LabImage from "./pages/LabImage";
import LabPresentation from "./pages/LabPresentation";
import LabCapstone from "./pages/LabCapstone";
import { useAutoForceSso } from "./lib/foundryAiSso";


import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRouter() {
  // FoundryAI: bounce anonymous visitors on protected routes to the SSO Hub.
  // Read auth from the context (was previously hardcoded to false which force-bounced logged-in users too).
  const { user } = useAuth();
  useAutoForceSso({ isAuthenticated: !!user, publicRoutes: ["/auth","/login","/signup","/register","/forgot-password","/reset-password","/about","/contact","/pricing","/careers","/terms","/privacy"] });

  const location = useLocation();

  // Route to AuthCallback when the URL carries an SSO session or OAuth code,
  // regardless of whether it landed in the query string or the hash fragment.
  const hasSsoSession = location.search?.includes('session_id=') || location.hash?.includes('session_id=');
  const hasOAuthCode = location.search?.includes('code=');
  const isNotAuthCallbackPath = location.pathname !== '/auth/callback';
  if ((hasSsoSession || hasOAuthCode) && isNotAuthCallbackPath) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected Routes */}
      <Route element={<ProtectedRoute><BrandProvider><DashboardLayout /></BrandProvider></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/scripts" element={<ScriptGenerator />} />
        <Route path="/images" element={<ImageGenerator />} />
        <Route path="/audio" element={<AudioGenerator />} />
        <Route path="/video" element={<VideoGenerator />} />
        <Route path="/library" element={<ContentLibrary />} />
        <Route path="/labs" element={<Labs />} />
        <Route path="/labs/image" element={<LabImage />} />
        <Route path="/labs/presentation" element={<LabPresentation />} />
        <Route path="/labs/capstone" element={<LabCapstone />} />
      </Route>
      
      {/* Image Editor - Full Screen (outside dashboard layout) */}
      <Route path="/editor" element={<ProtectedRoute><ImageEditor /></ProtectedRoute>} />
      
      {/* Subscription Page */}
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      
      {/* Settings Page */}
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
